// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { buildQuery, buildVariables } from "ra-data-hasura";
import { getPgauditVariables, getPgAudit } from "./ListPgAudit";
import { getElectoralLogVariables, getElectoralLog } from "./ListElectoralLog";
import { LIST_USERS, customBuildGetUsersVariables } from "./GetUsers";
import { getPermissions } from "./GetPermissions";
import { getRoles } from "./GetRoles";
import { isString } from "lodash";
import { COLUMNS_MAP } from "@/types/query";
import { GetCastVotesByIp } from "./GetCastVotesByIp";
import { GET_ELECTIONS_BY_EXTERNAL_ID } from "./GetElectionsByExternalID";
var UUID_REGEXP = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * `labels`/`annotations` are free-form jsonb columns. ra-data-hasura's default
 * jsonb filter tries to JSON.parse the typed text and use `_contains`, which
 * silently no-ops for plain text (see typeAwareKeyValueReducer in
 * ra-data-hasura). Route these through a raw query that casts the column to
 * text and does a substring search instead.
 */
function applyJsonbTextSearchFilter(filter, field) {
    var value = filter[field];
    if (typeof value === "string" && value.length > 0) {
        filter[field] = {
            format: "hasura-raw-query",
            value: { _cast: { String: { _ilike: "%".concat(value, "%") } } },
        };
    }
}
/**
 * `uuid` columns reject non-uuid input at the Postgres level, so a partial
 * value typed by the user (before finishing pasting a full id) would error
 * out the whole list query. Only forward the filter once it's a full uuid.
 */
function applyUuidFilter(filter, field) {
    var value = filter[field];
    if (typeof value !== "string" || value.length === 0) {
        return;
    }
    if (UUID_REGEXP.test(value)) {
        filter[field] = { format: "hasura-raw-query", value: { _eq: value } };
    }
    else {
        delete filter[field];
    }
}
var GRAPHQL_INT_MIN = -2147483648;
var GRAPHQL_INT_MAX = 2147483647;
var INTEGER_REGEXP = /^-?\d+$/;
var isIntType = function (type) {
    var namedType = type.kind === "NON_NULL" ? type.ofType : type;
    return namedType.kind === "SCALAR" && namedType.name === "Int";
};
/**
 * ra-data-hasura forwards filter values as they are, so a text input (or a
 * filter restored from the URL) sends an `Int` column a string, which Hasura
 * rejects. Send valid GraphQL Int values as numbers and leave anything else
 * as typed, so it is never rounded, truncated or turned into zero.
 */
function applyIntFilters(resources, resourceName, filter) {
    var _a;
    var fields = (_a = resources === null || resources === void 0 ? void 0 : resources.find(function (resource) { return resource.type.name === resourceName; })) === null || _a === void 0 ? void 0 : _a.type.fields;
    if (!fields) {
        return filter;
    }
    return Object.fromEntries(Object.entries(filter).map(function (_a) {
        var key = _a[0], value = _a[1];
        if (typeof value !== "string" || !INTEGER_REGEXP.test(value)) {
            return [key, value];
        }
        var field = fields.find(function (f) { return f.name === key; });
        var number = Number(value);
        return field &&
            isIntType(field.type) &&
            number >= GRAPHQL_INT_MIN &&
            number <= GRAPHQL_INT_MAX
            ? [key, number]
            : [key, value];
    }));
}
export var customBuildQuery = function (introspectionResults) { return function (raFetchType, resourceName, params) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3;
    var sort = params.sort;
    if (isString(resourceName) && raFetchType === "GET_LIST") {
        if (!(sort === null || sort === void 0 ? void 0 : sort.field) ||
            (COLUMNS_MAP[resourceName] && !COLUMNS_MAP[resourceName].includes(sort.field))) {
            params.sort = undefined;
        }
        var validFilters_1 = COLUMNS_MAP[resourceName];
        if (validFilters_1) {
            Object.keys(params.filter).forEach(function (f) {
                if (!validFilters_1.includes(f)) {
                    console.log("removing ".concat(resourceName, ".filter.").concat(f));
                    delete params.filter[f];
                }
            });
        }
        if (params.filter) {
            params.filter = applyIntFilters(introspectionResults === null || introspectionResults === void 0 ? void 0 : introspectionResults.resources, resourceName, params.filter);
        }
    }
    if (resourceName.startsWith("pgaudit") && raFetchType === "GET_LIST") {
        var resource = {
            type: {
                fields: [],
                name: resourceName,
            },
        };
        return {
            query: getPgAudit(params, resourceName),
            variables: getPgauditVariables(buildVariables(introspectionResults)(resource, raFetchType, params, null)),
            parseResponse: function (res) {
                var response = res.data.listPgaudit;
                var output = {
                    data: response.items,
                    total: response.total.aggregate.count,
                };
                return output;
            },
        };
    }
    else if (resourceName === "electoral_log" && raFetchType === "GET_LIST") {
        var validFilters_2 = [
            "election_event_id",
            "user_id",
            "username",
            "created",
            "statement_timestamp",
            "statement_kind",
        ];
        Object.keys(params.filter).forEach(function (f) {
            if (!validFilters_2.includes(f)) {
                delete params.filter[f];
            }
        });
        var resource = {
            type: {
                fields: [],
                name: resourceName,
            },
        };
        return {
            query: getElectoralLog(params),
            variables: getElectoralLogVariables(buildVariables(introspectionResults)(resource, raFetchType, params, null)),
            parseResponse: function (res) {
                var response = res.data.listElectoralLog;
                var output = {
                    data: response.items,
                    total: response.total.aggregate.count,
                };
                return output;
            },
        };
    }
    else if (resourceName === "sequent_backend_report" && raFetchType === "GET_LIST") {
        var ret = buildQuery(introspectionResults)(raFetchType, resourceName, params);
        if ((_a = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _a === void 0 ? void 0 : _a.order_by) {
            var validOrderBy_1 = [
                "id",
                "created_at",
                "election_id",
                "report_type",
                "template_alias",
                "encryption_policy",
            ];
            ret.variables.order_by = Object.fromEntries(Object.entries(((_b = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _b === void 0 ? void 0 : _b.order_by) || {}).filter(function (_a) {
                var key = _a[0];
                return validOrderBy_1.includes(key);
            }));
            if ((_c = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _c === void 0 ? void 0 : _c.order_by) {
                ret.variables.order_by = [__assign({}, ret.variables.order_by), { id: "asc" }];
            }
        }
        return ret;
    }
    else if (resourceName === "sequent_backend_tasks_execution" &&
        raFetchType === "GET_LIST") {
        var ret = buildQuery(introspectionResults)(raFetchType, resourceName, params);
        if ((_d = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _d === void 0 ? void 0 : _d.order_by) {
            var validOrderBy_2 = [
                "annotations",
                "created_at",
                "election_event_id",
                "end_at",
                "executed_by_user",
                "execution_status",
                "id",
                "labels",
                "logs",
                "name",
                "start_at",
                "tenant",
                "tenant_id",
                "type",
            ];
            ret.variables.order_by = Object.fromEntries(Object.entries(((_e = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _e === void 0 ? void 0 : _e.order_by) || {}).filter(function (_a) {
                var key = _a[0];
                return validOrderBy_2.includes(key);
            }));
        }
        return ret;
    }
    else if (resourceName === "sequent_backend_scheduled_event" &&
        raFetchType === "GET_LIST") {
        var ret = buildQuery(introspectionResults)(raFetchType, resourceName, params);
        if ((_f = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _f === void 0 ? void 0 : _f.order_by) {
            ret.variables.order_by = [__assign({}, ret.variables.order_by), { id: "asc" }];
        }
        var electionIds = (_k = (_j = (_h = (_g = params === null || params === void 0 ? void 0 : params.filter) === null || _g === void 0 ? void 0 : _g.event_payload) === null || _h === void 0 ? void 0 : _h.value) === null || _j === void 0 ? void 0 : _j._contains) === null || _k === void 0 ? void 0 : _k.election_id;
        if (electionIds) {
            var newAnd = ret.variables.where._and.filter(function (and) { return !("event_payload" in and); });
            newAnd.push({
                _or: __spreadArray(__spreadArray([], electionIds.map(function (electionId) { return ({
                    event_payload: {
                        _contains: {
                            election_id: electionId,
                        },
                    },
                }); }), true), [
                    {
                        event_payload: {
                            _contains: {
                                election_id: null,
                            },
                        },
                    },
                ], false),
            });
            ret.variables.where._and = newAnd;
        }
        return ret;
    }
    else if (resourceName === "sequent_backend_ballot_publication" &&
        raFetchType === "GET_LIST") {
        var ret = buildQuery(introspectionResults)(raFetchType, resourceName, params);
        if ((_m = (_l = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _l === void 0 ? void 0 : _l.where) === null || _m === void 0 ? void 0 : _m._and) {
            if (!((_o = params === null || params === void 0 ? void 0 : params.filter) === null || _o === void 0 ? void 0 : _o.election_id)) {
                ret.variables.where._and.push({
                    election_id: { _is_null: true },
                });
            }
            else {
                var indexToReplace = ret.variables.where._and.findIndex(function (el) { return el === null || el === void 0 ? void 0 : el.election_id; });
                ret.variables.where._and[indexToReplace] = {
                    election_ids: { _contains: [(_p = params === null || params === void 0 ? void 0 : params.filter) === null || _p === void 0 ? void 0 : _p.election_id] },
                };
            }
        }
        return ret;
    }
    else if (resourceName === "user" && raFetchType === "GET_LIST") {
        var resource = {
            type: {
                fields: [],
                name: "user",
            },
        };
        return {
            query: LIST_USERS,
            variables: customBuildGetUsersVariables(introspectionResults)(resource, raFetchType, params, null),
            parseResponse: function (res) {
                var response = res.data.get_users;
                var output = {
                    data: response.items,
                    total: response.total.aggregate.count,
                };
                return output;
            },
        };
    }
    else if (resourceName === "role" && raFetchType === "GET_LIST") {
        var resource = {
            type: {
                fields: [],
                name: "role",
            },
        };
        return {
            query: getRoles(params.filter),
            variables: buildVariables(introspectionResults)(resource, raFetchType, params, null),
            parseResponse: function (res) {
                var response = res.data.get_roles;
                var output = {
                    data: response.items,
                    total: response.total.aggregate.count,
                };
                return output;
            },
        };
    }
    else if (resourceName === "permission" && raFetchType === "GET_LIST") {
        var resource = {
            type: {
                fields: [],
                name: "role",
            },
        };
        return {
            query: getPermissions(params.filter),
            variables: buildVariables(introspectionResults)(resource, raFetchType, params, null),
            parseResponse: function (res) {
                var response = res.data.get_permissions;
                var output = {
                    data: response.items,
                    total: response.total.aggregate.count,
                };
                return output;
            },
        };
    }
    else if (resourceName === "ip_address" && raFetchType === "GET_LIST") {
        var resource = {
            type: {
                fields: [],
                name: "ip_address",
            },
        };
        return {
            query: GetCastVotesByIp(params),
            variables: buildVariables(introspectionResults)(resource, raFetchType, params, null),
            parseResponse: function (res) {
                var response = res.data.get_top_votes_by_ip;
                var output = {
                    data: response.items,
                    total: response.total.aggregate.count,
                };
                return output;
            },
        };
    }
    else if (resourceName === "sequent_backend_applications" && raFetchType === "GET_LIST") {
        var ret = buildQuery(introspectionResults)(raFetchType, resourceName, params);
        if ((_q = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _q === void 0 ? void 0 : _q.order_by) {
            var validOrderBy_3 = [
                "id",
                "created_at",
                "updated_at",
                "applicant_id",
                "verification_type",
                "status",
            ];
            ret.variables.order_by = Object.fromEntries(Object.entries(((_r = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _r === void 0 ? void 0 : _r.order_by) || {}).filter(function (_a) {
                var key = _a[0];
                return validOrderBy_3.includes(key);
            }));
        }
        var filter_1 = params.filter;
        var transformedRawParams = __assign({}, ret === null || ret === void 0 ? void 0 : ret.variables.where);
        var transformedParams_1 = ret === null || ret === void 0 ? void 0 : ret.variables.where["_and"];
        // Transform applicant_data
        Object.keys(filter_1).forEach(function (key) {
            if (key === "applicant_data" && typeof filter_1[key] === "object") {
                var flattened_1 = flattenObject(filter_1[key]);
                Object.keys(flattened_1).forEach(function (newField) {
                    var _a;
                    transformedParams_1.push({
                        applicant_data: {
                            _contains: (_a = {}, _a[newField] = flattened_1[newField], _a),
                        },
                    });
                });
            }
        });
        ret.variables.where = transformedRawParams;
        return ret;
    }
    else if (resourceName === "sequent_backend_election_by_external_id" &&
        raFetchType === "GET_MANY") {
        var externalIds = ((_s = params === null || params === void 0 ? void 0 : params.ids) !== null && _s !== void 0 ? _s : []).map(String);
        var electionEventId = (_y = (_v = (_u = (_t = params === null || params === void 0 ? void 0 : params.meta) === null || _t === void 0 ? void 0 : _t.filter) === null || _u === void 0 ? void 0 : _u.election_event_id) !== null && _v !== void 0 ? _v : (_x = (_w = params === null || params === void 0 ? void 0 : params.meta) === null || _w === void 0 ? void 0 : _w.filter) === null || _x === void 0 ? void 0 : _x.electionEventId) !== null && _y !== void 0 ? _y : null;
        return {
            query: GET_ELECTIONS_BY_EXTERNAL_ID,
            variables: {
                external_ids: externalIds,
                election_event_id: electionEventId,
            },
            parseResponse: function (res) {
                var _a, _b;
                var rows = (_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.sequent_backend_election) !== null && _b !== void 0 ? _b : [];
                return {
                    data: rows.map(function (r) { return (__assign(__assign({}, r), { id: r.external_id })); }),
                };
            },
        };
    }
    else if (resourceName === "sequent_backend_tally_session_execution" &&
        raFetchType === "GET_LIST" &&
        ((_z = params === null || params === void 0 ? void 0 : params.meta) === null || _z === void 0 ? void 0 : _z.latestPerTallySession)) {
        params.filter = __assign(__assign({}, params.filter), { distinct_on: ["tally_session_id"] });
        var ret = buildQuery(introspectionResults)(raFetchType, resourceName, params);
        if ((_0 = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _0 === void 0 ? void 0 : _0.order_by) {
            ret.variables.order_by = [
                { tally_session_id: "asc" },
                { created_at: "desc_nulls_last" },
                { id: "desc" },
            ];
        }
        return ret;
    }
    else if (resourceName === "sequent_backend_tally_sheet" && raFetchType === "GET_LIST") {
        applyJsonbTextSearchFilter(params.filter, "labels");
        applyJsonbTextSearchFilter(params.filter, "annotations");
        applyUuidFilter(params.filter, "import_id");
        if ((_1 = params === null || params === void 0 ? void 0 : params.meta) === null || _1 === void 0 ? void 0 : _1.distinctBallotBoxes) {
            // Show only one row per ballot box (area/contest/channel)
            params.filter = __assign(__assign({}, params.filter), { distinct_on: ["area_id", "contest_id", "channel"] });
        }
        var ret = buildQuery(introspectionResults)(raFetchType, resourceName, params);
        if (((_2 = params === null || params === void 0 ? void 0 : params.meta) === null || _2 === void 0 ? void 0 : _2.distinctBallotBoxes) && ((_3 = ret === null || ret === void 0 ? void 0 : ret.variables) === null || _3 === void 0 ? void 0 : _3.order_by)) {
            ret.variables.order_by = [
                { area_id: "asc" },
                { contest_id: "asc" },
                { channel: "asc" },
                { version: "desc" },
            ];
        }
        return ret;
    }
    return buildQuery(introspectionResults)(raFetchType, resourceName, params);
}; };
function flattenObject(obj, prefix) {
    if (prefix === void 0) { prefix = ""; }
    var result = {};
    Object.keys(obj).forEach(function (key) {
        var newKey = prefix ? "".concat(prefix, ".").concat(key) : key;
        if (typeof obj[key] === "object" && obj[key] !== null && !("_ilike" in obj[key])) {
            // Recursively flatten only if it's an object and doesn't have `_ilike`
            Object.assign(result, flattenObject(obj[key], newKey));
        }
        else if ("_ilike" in obj[key]) {
            // Extract `_ilike` value
            result[newKey] = obj[key]["_ilike"];
        }
    });
    return result;
}
